import config from './config.js';
// import projectConfig from '@project-config';
import {
  parseURL,
  generateSign,
  filterObjectNullVal,
  apiErrorCodeHandling,
  httpCodeHandling,
  dataEmitMessage,
  textDecode
} from './utils';
import { callInterceptor } from '../utils.js';

/**
 * 竞态请求管理器
 * 用于处理重复请求的取消和管理
 *
 * @class RaceRequestManager
 * @example
 * const manager = new RaceRequestManager();
 * const key = manager.generateKey(options);
 * if (manager.isPending(key)) {
 *   manager.cancel(key);
 * }
 * manager.add(key, abortController);
 */
class RaceRequestManager {
  constructor() {
    this.pendingRequests = new Map();
  }

  /**
   * 生成请求的唯一标识
   *
   * @param {Object} options - 请求配置
   * @param {string} options.url - 请求URL
   * @param {string} options.method - 请求方法
   * @param {Object} options.data - 请求数据
   * @return {string} 请求的唯一标识
   *
   * @example
   * const key = manager.generateKey({
   *   url: '/api/test',
   *   method: 'GET',
   *   data: { id: 1 }
   * });
   */
  generateKey(options) {
    const { url, method, data } = options;
    return `${method}:${url}:${JSON.stringify(data || {})}`;
  }

  /**
   * 检查请求是否正在进行中
   *
   * @param {string} key - 请求标识
   * @return {boolean} 是否正在进行中
   *
   * @example
   * if (manager.isPending(key)) {
   *   console.log('请求正在进行中');
   * }
   */
  isPending(key) {
    return this.pendingRequests.has(key);
  }

  /**
   * 添加正在进行的请求
   *
   * @param {string} key - 请求标识
   * @param {AbortController} abortController - 用于取消请求的控制器
   *
   * @example
   * const controller = new AbortController();
   * manager.add(key, controller);
   */
  add(key, abortController) {
    this.pendingRequests.set(key, abortController);
  }

  /**
   * 移除已完成的请求
   *
   * @param {string} key - 请求标识
   *
   * @example
   * manager.remove(key);
   */
  remove(key) {
    this.pendingRequests.delete(key);
  }

  /**
   * 取消指定的请求
   *
   * @param {string} key - 请求标识
   * @param {string} reason - 取消原因
   *
   * @example
   * manager.cancel(key, '重复请求');
   */
  cancel(key, reason = '请求被取消') {
    const abortController = this.pendingRequests.get(key);
    if (abortController) {
      abortController.abort(reason);
      this.remove(key);
    }
  }

  /**
   * 取消所有正在进行的请求
   *
   * @param {string} reason - 取消原因
   *
   * @example
   * manager.cancelAll('页面卸载');
   */
  cancelAll(reason = '批量取消') {
    this.pendingRequests.forEach((abortController, key) => {
      abortController.abort(reason);
    });
    this.pendingRequests.clear();
  }
}

/**
 * 拦截器管理类
 * 用于管理请求和响应拦截器的注册、执行和移除
 *
 * @class InterceptorManager
 * @example
 * const manager = new InterceptorManager();
 * const id = manager.use(onFulfilled, onRejected, { priority: 100 });
 * manager.eject(id);
 */
class InterceptorManager {
  constructor() {
    this.handlers = [];
  }

  /**
   * 添加拦截器
   *
   * @param {Function} fulfilled - 成功时的处理函数
   * @param {Function} rejected - 失败时的处理函数
   * @param {Object} options - 配置选项
   * @param {number} [options.priority=0] - 优先级，数值越大优先级越高
   * @param {Function} [options.runWhen] - 条件函数，返回true时才执行拦截器
   * @return {number} 拦截器ID，用于移除拦截器
   *
   * @example
   * // 添加请求拦截器
   * const id = interceptors.request.use(
   *   (config) => {
   *     config.headers.token = 'Bearer xxx';
   *     return config;
   *   },
   *   (error) => Promise.reject(error),
   *   { priority: 100 }
   * );
   */
  use(fulfilled, rejected, options = {}) {
    const handler = {
      fulfilled,
      rejected,
      priority: options.priority || 0,
      runWhen: options.runWhen,
      id: Date.now() + Math.random()
    };

    this.handlers.push(handler);
    // 按优先级排序，优先级高的在前
    this.handlers.sort((a, b) => b.priority - a.priority);

    return handler.id;
  }

  /**
   * 移除拦截器
   *
   * @param {number} id - 拦截器ID
   *
   * @example
   * interceptors.request.eject(id);
   */
  eject(id) {
    const index = this.handlers.findIndex((handler) => handler.id === id);
    if (index !== -1) {
      this.handlers.splice(index, 1);
    }
  }

  /**
   * 遍历执行拦截器
   *
   * @param {Function} fn - 对每个拦截器执行的函数
   *
   * @example
   * interceptors.request.each((interceptor) => {
   *   if (interceptor.runWhen && !interceptor.runWhen(config)) return;
   *   // 执行拦截器逻辑
   * });
   */
  each(fn) {
    this.handlers.forEach(fn);
  }

  /**
   * 清空所有拦截器
   *
   * @example
   * interceptors.request.clear();
   */
  clear() {
    this.handlers = [];
  }
}

class EventResponse {
  constructor() {
    this.events = {};
    this.request = {};
    this.options = {};
    // 初始化拦截器管理器
    this.interceptors = {
      request: new InterceptorManager(),
      response: new InterceptorManager()
    };
    // 初始化竞态请求管理器
    this.raceManager = new RaceRequestManager();
  }

  /**
   * 监听特定事件
   *
   * @param {string} event - 要监听的事件名称
   * @param {function} callback - 事件发生时执行的回调函数
   * @return {EventResponse} 返回当前实例，以便链式调用
   */
  on(event, callback) {
    this.events[event] = callback;
    return this;
  }

  /**
   * 触发事件
   *
   * @param {string} event - 要触发的事件名称
   * @param {...any} args - 传递给回调函数的参数
   * @return {EventResponse} 返回当前实例，以便链式调用
   */
  call(event, ...args) {
    setTimeout(() => {
      const allCallback = this.events['*'];
      if (allCallback) {
        allCallback(event, ...args);
      }
      const callback = this.events[event];
      if (callback) {
        callback(...args);
      }
    });
    return this;
  }
}

let stream = new EventResponse();

// 添加默认的请求拦截器 - 处理请求头
stream.interceptors.request.use(
  (config) => {
    // 设置默认Content-Type
    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'text/event-stream';
    }

    // 生成签名
    if (config.data) {
      const params = {
        ...parseURL(config.url),
        ...filterObjectNullVal(config.data)
      };
      config.headers.sign = generateSign(params);
    }

    // 这里可以添加token等认证信息
    // 如果需要token，可以通过以下方式获取：
    // const token = uni.getStorageSync('token') || localStorage.getItem('token');
    // if (token) {
    //   config.headers.Authorization = 'Bearer ' + token;
    // }

    return config;
  },
  (error) => Promise.reject(error),
  { priority: 0 }
);

// 添加默认的响应拦截器
stream.interceptors.response.use(
  (response) => {
    // 默认响应处理逻辑
    return response;
  },
  (error) => {
    // 默认错误处理逻辑
    return Promise.reject(error);
  },
  { priority: 0 }
);

/**
 * 处理竞态请求的拦截器函数
 * 检查是否存在相同的请求，如果存在则取消之前的请求
 *
 * @param {Object} options - 请求配置对象
 * @return {boolean} 是否允许继续执行请求
 *
 * @example
 * const canProceed = dealRaceRequest({
 *   url: '/api/test',
 *   method: 'GET',
 *   data: { id: 1 }
 * });
 */
const dealRaceRequest = (options) => {
  const requestKey = stream.raceManager.generateKey(options);

  // 如果存在相同的请求，取消之前的请求
  if (stream.raceManager.isPending(requestKey)) {
    stream.raceManager.cancel(requestKey, '重复请求被取消');
  }

  // 创建新的AbortController
  const abortController = new AbortController();
  stream.raceManager.add(requestKey, abortController);

  // 将AbortController添加到请求选项中
  options.signal = abortController.signal;
  options._requestKey = requestKey;

  return true;
};

const globalConfig = {
  ...config
  // ...(projectConfig || {})
};

/**
 * 执行请求拦截器链
 *
 * @param {Object} config - 请求配置对象
 * @return {Promise<Object>} 处理后的配置对象
 */
const executeRequestInterceptors = async (config) => {
  let processedConfig = config;

  // 按优先级顺序执行请求拦截器
  for (const interceptor of stream.interceptors.request.handlers) {
    try {
      // 检查是否满足执行条件
      if (
        typeof interceptor.runWhen === 'function' &&
        !interceptor.runWhen(processedConfig)
      ) {
        continue;
      }

      if (interceptor.fulfilled) {
        processedConfig = await interceptor.fulfilled(processedConfig);
      }
    } catch (error) {
      if (interceptor.rejected) {
        processedConfig = await interceptor.rejected(error);
      } else {
        throw error;
      }
    }
  }

  return processedConfig;
};

/**
 * 执行响应拦截器链
 *
 * @param {Object} response - 响应对象
 * @return {Promise<Object>} 处理后的响应对象
 */
const executeResponseInterceptors = async (response) => {
  let processedResponse = response;

  // 按优先级顺序执行响应拦截器
  for (const interceptor of stream.interceptors.response.handlers) {
    try {
      if (interceptor.fulfilled) {
        processedResponse = await interceptor.fulfilled(processedResponse);
      }
    } catch (error) {
      if (interceptor.rejected) {
        processedResponse = await interceptor.rejected(error);
      } else {
        throw error;
      }
    }
  }

  return processedResponse;
};

/**
 * 发送服务器发送事件（Server-Sent Events，SSE）请求
 * 这个函数准备必要的请求参数，包括解析 URL、处理数据、生成签名，并根据用户登录状态添加授权头。然后，它通过拦截器调用竞态请求处理逻辑，并根据处理结果决定是否发起请求。
 *
 * @param {Object} param - 请求参数对象
 * @param {string} param.url - 请求的URL
 * @param {string} [param.method='GET'] - 请求方法，默认为GET
 * @param {Object} [param.data={}] - 请求数据
 * @param {Object} [param.headers={}] - 请求头
 * @param {boolean} [param.enableRaceControl=true] - 是否启用竞态请求控制
 * @return {Promise} 一个 Promise, 当请求成功或失败时解析
 */
const sendSSE = async ({
  url,
  method = 'GET',
  data = {},
  headers = {},
  enableRaceControl = true
}) => {
  let requestUrl = globalConfig.BASE_API + url;
  let options = (stream.options = {
    url: requestUrl,
    method: method,
    headers: headers,
    data: data
  });

  try {
    // 执行请求拦截器
    options = await executeRequestInterceptors(options);
  } catch (error) {
    return Promise.reject(error);
  }

  return new Promise((resolve, reject) => {
    // 处理竞态请求逻辑
    if (enableRaceControl) {
      callInterceptor(dealRaceRequest, {
        args: [options],
        done: async (result) => {
          const requestSSE = async (options) => {
            try {
              let response;
              // #ifdef H5
              response = await fetchSSE(options);
              // #endif
              // #ifdef MP-WEIXIN
              response = await wxSSE(options);
              // #endif

              // 执行响应拦截器
              try {
                response = await executeResponseInterceptors(response);
              } catch (interceptorError) {
                // 清理竞态请求记录
                if (options._requestKey) {
                  stream.raceManager.remove(options._requestKey);
                }
                reject(interceptorError);
                return;
              }

              // 清理竞态请求记录
              if (options._requestKey) {
                stream.raceManager.remove(options._requestKey);
              }
              resolve(response);
              return response;
            } catch (error) {
              stream.call('onError', error);
              // 清理竞态请求记录
              if (options._requestKey) {
                stream.raceManager.remove(options._requestKey);
              }
              // 连接超时，重新请求
              if (error.errno == 5) {
                requestSSE(options);
                return;
              }
              reject(error);
            }
          };
          requestSSE(options);
        },
        canceled: () => {
          stream.call('onClose', 'canceled');
          reject('重复请求');
        }
      });
    } else {
      // 不启用竞态控制，直接执行请求
      const requestSSE = async (options) => {
        try {
          let response;
          // #ifdef H5
          response = await fetchSSE(options);
          // #endif
          // #ifdef MP-WEIXIN
          response = await wxSSE(options);
          // #endif

          // 执行响应拦截器
          try {
            response = await executeResponseInterceptors(response);
          } catch (interceptorError) {
            reject(interceptorError);
            return;
          }

          resolve(response);
          return response;
        } catch (error) {
          stream.call('onError', error);
          // 连接超时，重新请求
          if (error.errno == 5) {
            requestSSE(options);
            return;
          }
          reject(error);
        }
      };
      requestSSE(options);
    }
  });
};

/**
 * 使用 fetch API 发送 SSE 请求
 *
 * @param {Object} param - 请求参数对象
 * @param {string} param.url - 请求的URL
 * @param {string} [param.method='GET'] - 请求方法，默认为GET
 * @param {Object} [param.headers={}] - 请求头
 * @param {Object} [param.data={}] - 请求数据
 * @param {AbortSignal} [param.signal] - 用于取消请求的信号
 * @return {Promise} 一个 Promise，当初始请求完成、请求取消或请求出错时解析
 */
const fetchSSE = ({ url, method, headers, data, signal }) => {
  // 如果没有提供signal，创建一个新的AbortController
  if (!signal) {
    stream.request = new AbortController();
    signal = stream.request.signal;
  } else {
    // 使用提供的signal，但也保存引用以便取消
    stream.request = { signal, abort: () => signal.abort() };
  }

  return new Promise(async (resolve, reject) => {
    try {
      let requestParam = {
        method,
        headers,
        signal
      };
      if (method == 'POST') {
        requestParam.body = JSON.stringify({
          ...data
        });
      }
      if (method == 'GET') {
        let paramsKeys = Object.keys(data);
        let paramUrl = paramsKeys.reduce((p, n, index) => {
          return (
            p + `${n}=${data[n]}${index == paramsKeys.length - 1 ? '' : '&'}`
          );
        }, '?');
        url += paramUrl;
      }
      const response = await fetch(url, requestParam);
      if (!response.ok) {
        // 请求失败，
        let message = response.body;
        if (response.headers.get('content-type')?.includes('json')) {
          const json = await response.json();
          if (json.error?.message) {
            message = json.error?.message;
          }
        }
        reject(message);
      } else {
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            // 接收完成
            stream.call('onClose', done);
            resolve();
            return;
          }
          const line = dataEmitMessage(
            decoder.decode(value, {
              stream: true
            })
          );
          if (Array.isArray(line) && line.length <= 0) {
            reject({ line, msg: `${url} sselisten` });
          } else {
            stream.call('onProgress', line);
          }
        }
      }
    } catch (error) {
      // 检查是否是取消错误
      if (error.name === 'AbortError') {
        stream.call('onClose', 'aborted');
        resolve();
      } else {
        reject(error);
      }
    }
  });
};

/**
 * 使用 uni.request 发送 SSE 请求并处理响应
 *
 * @param {Object} param - 请求参数对象
 * @param {string} param.url - 请求的URL
 * @param {string} [param.method='GET'] - 请求方法，默认为GET
 * @param {Object} [param.headers={}] - 请求头
 * @param {Object} [param.data={}] - 请求数据
 * @return {Promise} 一个 Promise，当初始请求完成、请求取消或请求出错时解析
 */
const wxSSE = ({ url, method, headers, data }) => {
  return new Promise((resolve, reject) => {
    stream.request = uni.request({
      url,
      method,
      header: headers,
      data: {
        ...data
      },
      enableChunked: true,
      complete(res) {},
      success(res) {
        if (res.statusCode !== 200) {
          httpCodeHandling(res);
          reject(res);
        }
        resolve(res);
      },
      fail(res) {
        reject(res);
      }
    });
    stream.request.onChunkReceived((res) => {
      const line = dataEmitMessage(textDecode(new Uint8Array(res.data)));
      if (Array.isArray(line) && line.length <= 0) {
        reject({ line, msg: `${url} sselisten` });
      } else {
        stream.call('onProgress', line);
      }
    });
  });
};

/**
 * 取消正在进行的 SSE 请求
 *
 * @param {function} [cb=() => {}] - 请求取消后的回调函数
 */
const cancelStream = (cb = () => {}) => {
  if (!stream.request) return;

  // #ifdef MP-WEIXIN
  if (stream.request.offChunkReceived) {
    stream.request.offChunkReceived();
  }
  // #endif

  if (stream.request.abort) {
    stream.request.abort();
  }

  // 清理竞态请求记录
  if (stream.options._requestKey) {
    stream.raceManager.remove(stream.options._requestKey);
  }

  cb(stream);
};

/**
 * 取消所有正在进行的SSE请求
 *
 * @param {string} reason - 取消原因
 * @param {function} [cb=() => {}] - 请求取消后的回调函数
 *
 * @example
 * // 页面卸载时取消所有请求
 * cancelAllStreams('页面卸载');
 */
const cancelAllStreams = (reason = '批量取消', cb = () => {}) => {
  stream.raceManager.cancelAll(reason);
  cb(stream);
};

/**
 * 添加请求拦截器
 *
 * @param {Function} onFulfilled - 请求成功时的处理函数
 * @param {Function} onRejected - 请求失败时的处理函数
 * @param {Object} options - 配置选项
 * @param {number} [options.priority=0] - 优先级，数值越大优先级越高
 * @param {Function} [options.runWhen] - 条件函数，返回true时才执行拦截器
 * @return {number} 拦截器ID，用于移除拦截器
 *
 * @example
 * // 添加请求拦截器
 * const requestId = addSSERequestInterceptor(
 *   (config) => {
 *     console.log('发送SSE请求:', config.url);
 *     config.headers['X-Custom-Header'] = 'custom-value';
 *     return config;
 *   },
 *   (error) => {
 *     console.error('请求拦截器错误:', error);
 *     return Promise.reject(error);
 *   },
 *   { priority: 100 }
 * );
 */
const addSSERequestInterceptor = (onFulfilled, onRejected, options = {}) => {
  return stream.interceptors.request.use(onFulfilled, onRejected, options);
};

/**
 * 添加响应拦截器
 *
 * @param {Function} onFulfilled - 响应成功时的处理函数
 * @param {Function} onRejected - 响应失败时的处理函数
 * @param {Object} options - 配置选项
 * @param {number} [options.priority=0] - 优先级，数值越大优先级越高
 * @return {number} 拦截器ID，用于移除拦截器
 *
 * @example
 * // 添加响应拦截器
 * const responseId = addSSEResponseInterceptor(
 *   (response) => {
 *     console.log('SSE响应处理:', response);
 *     return response;
 *   },
 *   (error) => {
 *     console.error('响应拦截器错误:', error);
 *     return Promise.reject(error);
 *   },
 *   { priority: 100 }
 * );
 */
const addSSEResponseInterceptor = (onFulfilled, onRejected, options = {}) => {
  return stream.interceptors.response.use(onFulfilled, onRejected, options);
};

/**
 * 移除请求拦截器
 *
 * @param {number} id - 拦截器ID
 *
 * @example
 * removeSSERequestInterceptor(requestId);
 */
const removeSSERequestInterceptor = (id) => {
  stream.interceptors.request.eject(id);
};

/**
 * 移除响应拦截器
 *
 * @param {number} id - 拦截器ID
 *
 * @example
 * removeSSEResponseInterceptor(responseId);
 */
const removeSSEResponseInterceptor = (id) => {
  stream.interceptors.response.eject(id);
};

// 添加默认导出
const requestSSE = {
  stream,
  sendSSE,
  cancelStream,
  cancelAllStreams,
  addSSERequestInterceptor,
  addSSEResponseInterceptor,
  removeSSERequestInterceptor,
  removeSSEResponseInterceptor
};

export default requestSSE;

export {
  stream,
  sendSSE,
  cancelStream,
  cancelAllStreams,
  addSSERequestInterceptor,
  addSSEResponseInterceptor,
  removeSSERequestInterceptor,
  removeSSEResponseInterceptor
};
